import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Activity, Brain, TrendingDown, Sparkles, Wand2, Filter, Zap, Repeat } from "lucide-react";
import api from "@/lib/api";

function retColor(pct) {
    if (pct == null) return "#555";
    return pct >= 50 ? "#39FF14" : pct >= 30 ? "#FFD700" : "#FF4757";
}
const VERDICT = { green: "#39FF14", yellow: "#FFD700", red: "#FF4757", pending: "#666" };
const FILTER_COLOR = { pass: "#39FF14", warn: "#FFD700", fail: "#FF4757", pending: "#555", na: "#3a3a3a" };
const FILTER_META = [
    { key: "swipe", label: "Swipe / Gancho", icon: Zap },
    { key: "retention", label: "Retención", icon: Activity },
    { key: "loop", label: "Bucle", icon: Repeat },
];
const STATUS_TXT = { pass: "PASA", warn: "AL LÍMITE", fail: "NO PASA", pending: "SIN DATOS", na: "N/A" };

export default function Retention() {
    const [analysis, setAnalysis] = useState({});
    const [loadingId, setLoadingId] = useState(null);
    const [rewriteId, setRewriteId] = useState(null);
    const qc = useQueryClient();

    const { data: metrics = [], isLoading } = useQuery({
        queryKey: ["metrics"],
        queryFn: async () => (await api.get("/metrics")).data,
    });

    const { data: filters = [] } = useQuery({
        queryKey: ["algorithm-filters"],
        queryFn: async () => (await api.get("/algorithm-filters")).data,
    });

    const { data: autoOpt } = useQuery({
        queryKey: ["auto-optimize"],
        queryFn: async () => (await api.get("/governance/auto-optimize")).data,
    });
    const toggleOpt = useMutation({
        mutationFn: async (enabled) => (await api.post("/governance/auto-optimize", { enabled })).data,
        onSuccess: (d) => {
            toast.success(d.enabled
                ? "Auto-optimización ACTIVADA — el orquestador rehará solo los videos con caída fuerte"
                : "Auto-optimización desactivada");
            qc.invalidateQueries({ queryKey: ["auto-optimize"] });
        },
        onError: () => toast.error("No se pudo cambiar la auto-optimización"),
    });

    const setCtr = async (video_id, val) => {
        try {
            await api.post(`/videos/${video_id}/ctr`, { ctr: val === "" ? null : val });
            toast.success("CTR de Studio guardado — veredicto actualizado");
            qc.invalidateQueries({ queryKey: ["metrics"] });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "CTR inválido");
        }
    };

    const analyze = async (m) => {
        setLoadingId(m.video_id);
        try {
            const { data } = await api.post(`/videos/${m.video_id}/retention-analysis`);
            if (!data.available) {
                toast.info(data.message);
            } else {
                setAnalysis((p) => ({ ...p, [m.video_id]: data }));
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo analizar");
        } finally {
            setLoadingId(null);
        }
    };

    const rewrite = async (m, a) => {
        setRewriteId(m.video_id);
        try {
            await api.post(`/videos/${m.video_id}/rewrite`, {
                hook: a.analysis.hook_sugerido,
                fixes: a.analysis.arreglos || [],
            });
            toast.success("Nueva versión en producción con el gancho reforzado — aparecerá en Aprobaciones");
            qc.invalidateQueries({ queryKey: ["jobs"] });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo reescribir");
        } finally {
            setRewriteId(null);
        }
    };

    const withData = metrics.filter((m) => m.avg_retention != null).sort((a, b) => b.views - a.views);

    return (
        <div className="space-y-6" data-testid="retention-page">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="flex items-center gap-2 font-head text-2xl">
                        <Activity size={22} className="text-primary" /> Retención & Storytelling
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        ¿La historia retiene? El orquestador lee tu curva real y te dice dónde se va el
                        espectador y cómo reescribir para capturar más atención.
                    </p>
                </div>
                <button
                    data-testid="auto-optimize-toggle-btn"
                    onClick={() => toggleOpt.mutate(!autoOpt?.enabled)}
                    disabled={toggleOpt.isPending}
                    className={`flex shrink-0 items-center gap-2 border px-4 py-2.5 text-[11px] uppercase tracking-wider transition-colors ${
                        autoOpt?.enabled
                            ? "border-primary bg-primary text-black"
                            : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                    }`}
                >
                    <Wand2 size={14} />
                    {autoOpt?.enabled ? "Auto-optimización: ON" : "Activar auto-optimización"}
                </button>
            </div>
            <p className="-mt-3 text-[11px] text-muted-foreground">
                Con la auto-optimización activa, el orquestador detecta los videos con caída fuerte
                (retención &lt;45%) y produce solo una versión mejorada con mejor gancho — sin que se lo pidas.
            </p>

            {isLoading && <div className="text-sm text-muted-foreground">Cargando…</div>}

            {/* --- Panel de Filtros del Algoritmo --- */}
            <div className="border border-border" data-testid="algorithm-filters-panel">
                <div className="flex items-center gap-2 border-b border-border bg-secondary/30 px-4 py-3">
                    <Filter size={16} className="text-primary" />
                    <span className="font-head text-sm">Panel de Filtros del Algoritmo</span>
                    <span className="ml-auto text-[11px] text-muted-foreground">
                        Qué filtro pasa cada video: swipe · retención · bucle
                    </span>
                </div>
                <p className="border-b border-border px-4 py-2 text-[11px] text-muted-foreground">
                    Los videos que marcan <b className="text-[#FF4757]">NO PASA</b> en algún filtro se
                    <b className="text-foreground"> reescriben automáticamente</b> con un gancho/cierre reforzado
                    (una versión mejorada por ciclo) y se reintenta hasta que pasen.
                </p>
                {filters.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground">
                        Aún no hay datos de filtros (YouTube tarda 24-48h en entregar la curva de retención).
                    </div>
                ) : (
                    <div className="divide-y divide-border">
                        {filters.map((v) => (
                            <div key={v.video_id} className="flex flex-wrap items-center gap-4 p-4"
                                 data-testid={`filter-row-${v.video_id}`}>
                                <div className="min-w-0 flex-1">
                                    <a href={v.url} target="_blank" rel="noreferrer"
                                       className="truncate font-medium hover:text-primary">{v.title}</a>
                                    <div className="mt-0.5 flex items-center gap-3 text-[11px] text-muted-foreground">
                                        <span className="uppercase">{v.format}</span>
                                        <span>{v.views?.toLocaleString("es")} vistas</span>
                                        <span>Pasa <b className="text-foreground">{v.passed}/3</b> filtros</span>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {FILTER_META.map(({ key, label, icon: Ico }) => {
                                        const f = v.filters[key] || {};
                                        const c = FILTER_COLOR[f.status] || "#555";
                                        return (
                                            <div key={key} title={f.note || ""}
                                                 data-testid={`filter-${key}-${v.video_id}`}
                                                 className="flex min-w-[104px] flex-col gap-1 border border-border px-2.5 py-1.5"
                                                 style={{ borderColor: c + "66" }}>
                                                <span className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-muted-foreground">
                                                    <Ico size={11} style={{ color: c }} /> {label}
                                                </span>
                                                <span className="flex items-center justify-between">
                                                    <span className="text-[10px] font-bold" style={{ color: c }}>
                                                        {STATUS_TXT[f.status]}
                                                    </span>
                                                    <span className="text-[11px] text-foreground">
                                                        {f.value != null ? `${f.value}%` : "—"}
                                                    </span>
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {!isLoading && withData.length === 0 && (
                <div className="border border-border p-6 text-sm text-muted-foreground">
                    Aún no hay datos de retención (YouTube tarda 24-48h). Sincroniza tu canal en unas horas.
                </div>
            )}

            <div className="space-y-3">
                {withData.map((m) => {
                    const a = analysis[m.video_id];
                    return (
                        <div key={m.video_id} className="border border-border" data-testid={`ret-row-${m.video_id}`}>
                            <div className="flex flex-wrap items-center gap-4 p-4">
                                <div className="min-w-0 flex-1">
                                    <div className="truncate font-medium">{m.title}</div>
                                    <div className="mt-0.5 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                            <span className="h-2 w-2 rounded-full" style={{ background: VERDICT[m.verdict] || "#666" }} />
                                            {m.verdict}
                                        </span>
                                        <span className="uppercase">{m.format}</span>
                                        <span>{m.views?.toLocaleString("es")} vistas</span>
                                        <span>Engagement: <b className="text-foreground">{m.engagement_rate}%</b></span>
                                        {m.hook_retention != null && (
                                            <span>Gancho (0-3s): <b style={{ color: retColor(m.hook_retention) }}>{m.hook_retention}%</b></span>
                                        )}
                                        {m.retention_drop_at != null && (
                                            <span className="flex items-center gap-1 text-[#ff5964]">
                                                <TrendingDown size={12} /> Mayor caída en {m.retention_drop_at}%
                                            </span>
                                        )}
                                        <span className="flex items-center gap-1" title="Pega tu CTR desde YouTube Studio (la API no lo entrega)">
                                            CTR Studio
                                            <input
                                                data-testid={`ctr-input-${m.video_id}`}
                                                type="number" step="0.1" defaultValue={m.ctr ?? ""} placeholder="—"
                                                onKeyDown={(e) => { if (e.key === "Enter") setCtr(m.video_id, e.target.value); }}
                                                onBlur={(e) => { if ((e.target.value || "") !== String(m.ctr ?? "")) setCtr(m.video_id, e.target.value); }}
                                                className="w-14 border border-border bg-transparent px-1 py-0.5 text-[11px] text-foreground"
                                            />%
                                        </span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-head text-2xl" style={{ color: retColor(m.avg_retention) }}>
                                        {m.avg_retention != null ? `${m.avg_retention}%` : "—"}
                                    </div>
                                    <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Retención media</div>
                                </div>
                                <button
                                    data-testid={`analyze-btn-${m.video_id}`}
                                    onClick={() => analyze(m)}
                                    disabled={loadingId === m.video_id}
                                    className="flex items-center gap-2 border border-primary px-3 py-2 text-[11px] uppercase tracking-wider text-primary transition-colors hover:bg-primary hover:text-black disabled:opacity-40"
                                >
                                    <Brain size={14} />
                                    {loadingId === m.video_id ? "Analizando…" : "Analizar con IA"}
                                </button>
                            </div>

                            {a?.analysis && (
                                <div className="border-t border-border bg-secondary/30 p-4 text-sm" data-testid={`analysis-${m.video_id}`}>
                                    <div className="mb-2 flex items-center gap-2">
                                        <Sparkles size={14} className="text-primary" />
                                        <span className="font-head text-xs">Diagnóstico del orquestador</span>
                                        <span className="ml-auto bg-secondary px-2 py-0.5 text-[10px] uppercase">{a.analysis.veredicto}</span>
                                    </div>
                                    <p className="text-muted-foreground"><b className="text-foreground">Punto de fuga:</b> {a.analysis.punto_de_fuga}</p>
                                    <ul className="mt-2 space-y-1">
                                        {(a.analysis.arreglos || []).map((x, i) => (
                                            <li key={i} className="flex gap-2"><span className="text-primary">▸</span>{x}</li>
                                        ))}
                                    </ul>
                                    {a.analysis.hook_sugerido && (
                                        <div className="mt-3 border-l-2 border-primary pl-3">
                                            <span className="text-[10px] uppercase tracking-widest text-primary">Gancho sugerido (0-3s)</span>
                                            <p className="font-medium">“{a.analysis.hook_sugerido}”</p>
                                            <button
                                                data-testid={`rewrite-btn-${m.video_id}`}
                                                onClick={() => rewrite(m, a)}
                                                disabled={rewriteId === m.video_id}
                                                className="mt-2 flex items-center gap-2 border border-primary bg-primary px-3 py-2 text-[11px] uppercase tracking-wider text-black transition-colors hover:bg-primary/80 disabled:opacity-40"
                                            >
                                                <Wand2 size={13} />
                                                {rewriteId === m.video_id ? "Regenerando…" : "Reescribir y regenerar (1 clic)"}
                                            </button>
                                            <p className="mt-1 text-[10px] text-muted-foreground">
                                                Aplica este gancho + los arreglos y produce una versión nueva a la cola de Aprobaciones.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
